import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { completeRemoteLockOauth, getRemoteLockErrorMessage } from "../utils/remotelock";

const PENDING_REMOTELOCK_OAUTH_KEY = "pending_remotelock_oauth";
const INTEGRATIONS_TOAST_STORAGE_KEY = "pending_integration_toast";

/**
 * Public route: /integrations/remotelock/callback?code=…&state=…
 * Mirrors HostfullyCallbackScreen. The code is exchanged server-side; this
 * screen only forwards code+state for the signed-in host.
 */
const RemoteLockCallbackScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hostInfo } = useSelector((state) => state.host);

  const [statusText, setStatusText] = useState("Preparing callback...");
  const [errorText, setErrorText] = useState("");
  const startedRef = useRef(false);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const code = (query.get("code") || "").trim();
  const state = (query.get("state") || "").trim();
  const providerError = (query.get("error") || "").trim();

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (providerError) {
      setErrorText(`RemoteLock authorization was not successful (${providerError}).`);
      sessionStorage.removeItem(PENDING_REMOTELOCK_OAUTH_KEY);
      return;
    }
    if (!code || !state) {
      setErrorText("Missing OAuth callback parameters. Please reconnect RemoteLock from Integrations.");
      sessionStorage.removeItem(PENDING_REMOTELOCK_OAUTH_KEY);
      return;
    }

    sessionStorage.setItem(PENDING_REMOTELOCK_OAUTH_KEY, JSON.stringify({ code, state, createdAt: Date.now() }));

    if (!hostInfo?._id) {
      setStatusText("Sign in required. Redirecting to login...");
      navigate("/login", { replace: true });
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        setStatusText("Connecting RemoteLock account...");
        const result = await completeRemoteLockOauth(hostInfo._id, { code, state });
        if (cancelled) return;
        sessionStorage.removeItem(PENDING_REMOTELOCK_OAUTH_KEY);
        sessionStorage.setItem(
          INTEGRATIONS_TOAST_STORAGE_KEY,
          JSON.stringify({
            type: "success",
            message: result?.accountEmail
              ? `RemoteLock connected (${result.accountEmail}). Discovering your locks…`
              : "RemoteLock connected. Discovering your locks…",
          })
        );
        navigate("/integrations", { replace: true });
      } catch (error) {
        if (cancelled) return;
        sessionStorage.removeItem(PENDING_REMOTELOCK_OAUTH_KEY);
        // 401/440 are handled by the shared request interceptor (redirect to login).
        setErrorText(getRemoteLockErrorMessage(error, "Failed to complete RemoteLock authorization."));
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [code, state, providerError, hostInfo?._id, navigate]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
      <div className="w-full max-w-lg rounded-[16px] border border-[#EAEAEA] bg-[#F7F7F8] p-8">
        <h1 className="text-2xl font-bold text-[#111111] mb-3">Connecting RemoteLock</h1>
        {!errorText ? (
          <p className="text-sm text-[#5F6368]">{statusText}</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-red-600">{errorText}</p>
            <div className="flex items-center gap-4">
              <Link to="/integrations" className="text-sm font-medium text-[#111111] hover:underline">
                Back to Integrations
              </Link>
              <Link to="/login" className="text-sm font-medium text-[#5F6368] hover:text-[#111111]">
                Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RemoteLockCallbackScreen;
